import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import {
  type CaptionAudit,
  type ClipItem,
  type FramingMode,
  type EditCommand,
  type Edl,
  type LayoutPreset,
  type ShortsParams,
  DEFAULT_TRACK_IDS,
  LAYOUT_PRESETS,
  isOurPip,
  keepWidth,
  planFraming,
  readFraming,
} from '@repo/editor-core';
import { ThemeToggle, useToast } from '@repo/ui';
import type { AssetResolver } from '@/engine';
import { useEditorState } from './useEditorState';
import { useEngine, usePlayerState } from './useEngine';
import { PreviewPane } from './PreviewPane';
import { Timeline } from './Timeline';
import { CaptionModal } from './CaptionModal';
import { type GifSearch, GifSearchModal } from './GifSearchModal';
import { type AgentChatApi, AgentPanel } from './AgentPanel';
import { type SettingsApi, type SilencePreviewItem, SettingsPanel } from './SettingsPanel';
import { type VocabularyApi, VocabularyModal } from './VocabularyModal';
import { WebcamPipPreview } from './WebcamPipPreview';
import { type CursorTarget, computeTarget } from './choreography';
import type { ShortsBranding } from './branding';
import { humanError } from '@/lib/api-error';
import { useUnsavedGuard } from '@/lib/use-unsaved-guard';

const DEFAULT_CAPTION_FRAMES = 60;
/** 레이아웃 프리셋 표시명. 'full'이 쇼츠 기본형(영상이 화면 전체). */
const LAYOUT_LABELS: Record<LayoutPreset, string> = {
  full: '전체',
  basic: '기본',
  expanded: '확대',
  /*
   * ⚠️ 이 선택기는 `LAYOUT_PRESETS`(사람이 고를 수 있는 것)를 돌므로 이 라벨은 화면에
   *    나오지 않는다. `bands` 는 자동 생성 전용이다(`AUTO_ONLY_LAYOUT_PRESETS`).
   *    그래도 여기 있어야 한다 — 이 Record 가 **전수**라서, 없으면 tsc 가 막는다.
   *    그 막힘이 곧 «새 프리셋을 UI 에 노출할지 결정하라» 는 신호다.
   */
  bands: '밴드(자동)',
};
const OVERLAY_TRACK = 'overlay-1';
const DEFAULT_GIF_FRAMES = 60;
const MIN_GIF_FRAMES = 15; // 0.5s
const MAX_GIF_FRAMES = 300; // 10s — GIFs are short; guards against bad metadata
const DEFAULT_GIF_RECT = { x: 0.6, y: 0.65, w: 0.35, h: 0.2 };

/** Read a video's length in frames (@30fps) via a metadata-only load. */
function probeVideoDurationFrames(url: string, fallback: number): Promise<number> {
  return new Promise((resolve) => {
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.muted = true;
    const finish = (frames: number) => {
      v.removeAttribute('src');
      v.load();
      resolve(frames);
    };
    v.onloadedmetadata = () => {
      const f = Math.round((v.duration || 0) * 30);
      finish(Number.isFinite(f) && f > 0 ? f : fallback);
    };
    v.onerror = () => finish(fallback);
    v.src = url;
  });
}

/**
 * 소스의 가로세로비를 metadata 만 읽어서 알아낸다.
 *
 * 🔴 16:9 로 못 박지 않는 이유: 프레이밍이 «세로 화면에 남길 폭»을 이 값으로 계산한다.
 * 틀리면 사용자가 고른 자리와 실제로 남는 자리가 어긋나고, **눈으로는 못 잡는다.**
 * (지금 소스는 사실상 전부 1920×1080 이지만, 그건 «지금»의 사실이지 규칙이 아니다.)
 */
function probeVideoAspect(url: string): Promise<number | null> {
  return new Promise((resolve) => {
    const v = document.createElement('video');
    v.preload = 'metadata';
    v.muted = true;
    const finish = (a: number | null) => {
      v.removeAttribute('src');
      v.load();
      resolve(a);
    };
    v.onloadedmetadata = () =>
      finish(v.videoWidth > 0 && v.videoHeight > 0 ? v.videoWidth / v.videoHeight : null);
    v.onerror = () => finish(null);
    v.src = url;
  });
}

/**
 * 설정 패널이 서버와 주고받는 것.
 *
 * 동작(무음 컷·맛보기·얼굴)은 **커맨드 목록**을 돌려준다 — 셸이 그걸 커맨드 버스로
 * 재생해야 실행취소가 이어지고, 다음 저장이 서버 결과를 덮어쓰지 않는다.
 * 이 규약은 AI 패널(`onEdit`)과 같다.
 */
export interface SettingsIntegration {
  params: ShortsParams;
  /** 저장 결과. 서버가 EDL 을 고쳤으면(자막 on/off) 그 EDL 을 돌려준다. */
  onParamsChange: (next: ShortsParams) => Promise<Edl | void> | void;
  silencePreview: SilencePreviewItem[] | null;
  webcamConfigured: boolean;
  /** 이 클립에 «어디가 터지는지» 근거가 있는가 (길이 자르기·맛보기의 전제). */
  hasBeats: boolean;
  /** 자막 자기검사 — 규칙이 결과를 몇 번 고쳤나. */
  captionAudit: CaptionAudit | null;
  vocabulary: VocabularyApi;
  /** 화면의(아직 저장 안 된) EDL 을 받아 적용 커맨드를 돌려준다. */
  applySilence: (edl: Edl) => Promise<EditCommand[]>;
  applyLength: (edl: Edl) => Promise<EditCommand[]>;
  downloadSource: () => void;
  applyColdOpen: (edl: Edl) => Promise<EditCommand[]>;
  applyWebcamPip: (edl: Edl) => Promise<EditCommand[]>;
  downloadSrt: (timebase: 'timeline' | 'source') => void;
}

export interface EditorShellProps {
  initialEdl: Edl;
  resolver: AssetResolver;
  title: string;
  /** 저장. 서버가 EDL을 갱신했으면 그 EDL을 돌려준다(자막 재생성 등). */
  onSave?: (edl: Edl) => Promise<Edl | void> | void;
  onExport?: () => Promise<void> | void;
  saving?: boolean;
  exportStatus?: string | null;
  /** AI agent chat (connected mode only). Omit to hide the agent panel. */
  agent?: AgentChatApi;
  /**
   * 편집 설정 패널(연결 모드 전용). 없으면 탭이 안 뜨고 AI 패널만 보인다.
   *
   * 왜 탭인가: 설정·채팅·타임라인 셋이 오른쪽 한 칸의 높이를 나눠 가지면 세로로
   * 눌려서 채팅이 0 높이가 된다(목업 B안에서 실제로 깨졌다). 한 번에 하나만 보이면
   * 높이 싸움이 없어지고, 스트리머 리뷰에서 나온 «설정과 채팅 중 뭘 먼저 하냐»
   * 혼란도 같이 풀린다(`docs/shorts-editor-input-redesign.md` §10.6).
   */
  settings?: SettingsIntegration;
  /** GIF search (connected mode only). Omit to hide the GIF picker. */
  gifSearch?: GifSearch;
  /** Common branding overlays (logo/channel/CTA). Omit → editor default preset. */
  branding?: ShortsBranding;
  /** 소스 클립 길이(초). 우측 트림이 소스 밖을 읽지 않도록 클램프하는 데 쓴다. */
  sourceDurationSec?: number;
  /** Optional notice row (lock/auth state). */
  banner?: ReactNode;
}

export function EditorShell({
  initialEdl,
  resolver,
  title,
  onSave,
  onExport,
  saving,
  exportStatus,
  agent,
  settings,
  gifSearch,
  branding,
  sourceDurationSec,
  banner,
}: EditorShellProps) {
  const editor = useEditorState(initialEdl);
  const engine = useEngine(resolver);
  const { toast } = useToast();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [captionOpen, setCaptionOpen] = useState(false);
  const [gifOpen, setGifOpen] = useState(false);
  const [agentCursor, setAgentCursor] = useState<CursorTarget | null>(null);
  /**
   * 오른쪽 패널 탭. 기본은 «설정» — 처음 여는 사람이 먼저 볼 것은 «무엇을 만들지»이고,
   * 채팅은 «어떤 장면을 남길지»를 덧붙이는 자리다(D2 역할 분리).
   */
  const [rightTab, setRightTab] = useState<'settings' | 'agent'>('settings');
  const [vocabOpen, setVocabOpen] = useState(false);
  const [settingsBusy, setSettingsBusy] = useState(false);
  /*
   * 쇼츠 앱 UI 가이드. **기본은 켬** — 자막·로고가 앱에 가리는 사고가 이 도구의
   * 주된 실패 모드라, 보이는 게 기본이어야 한다. 끌 수 있게 둔 건 결과물 자체를
   * 그대로 확인하고 싶을 때가 있어서다.
   */
  const [showGuide, setShowGuide] = useState(true);
  // Local draft for the title input; synced from the EDL (undo / agent edits).
  const [titleDraft, setTitleDraft] = useState(initialEdl.title?.text ?? '');
  // The agent holds an exclusive edit turn: manual editing is locked while it runs.
  const [agentBusy, setAgentBusy] = useState(false);
  const agentBusyRef = useRef(false);
  agentBusyRef.current = agentBusy;

  // 저장 안 한 편집·전송 중인 저장을 안고 탭이 닫히지 않게 막는다.
  useUnsavedGuard(editor.dirty || saving === true);

  // Subscribe to transport state so the playhead passed to edit tools is fresh,
  // and so the preview can show a play/pause affordance (no separate transport bar).
  const { currentFrame, playing } = usePlayerState(engine?.player ?? null);

  // Fresh refs for handlers that shouldn't re-bind every frame.
  const edlRef = useRef(editor.edl);
  edlRef.current = editor.edl;
  const selRef = useRef(selectedId);
  selRef.current = selectedId;
  const frameRef = useRef(currentFrame);
  frameRef.current = currentFrame;

  const onError = useCallback((msg: string) => toast(msg, 'error'), [toast]);

  // Keep the title input in sync when the EDL's title changes elsewhere.
  const edlTitle = editor.edl.title?.text ?? '';
  useEffect(() => {
    setTitleDraft(edlTitle);
  }, [edlTitle]);

  // Push common branding into the compositor, then re-render the current frame.
  useEffect(() => {
    if (!engine || !branding) return;
    engine.compositor.setBranding(branding);
    engine.player.setEdl(edlRef.current);
  }, [engine, branding]);

  const setLayout = useCallback(
    (layout: LayoutPreset) => {
      if (editor.edl.layout === layout) return;
      const err = editor.dispatch({ op: 'setLayout', layout });
      if (err) toast(err, 'error');
    },
    [editor, toast]
  );

  const commitTitle = useCallback(() => {
    const text = titleDraft.trim();
    if (text === (editor.edl.title?.text ?? '')) return;
    if (!text) return; // clearing the title is not supported in this phase
    const err = editor.dispatch({ op: 'setTitle', text });
    if (err) toast(err, 'error');
  }, [titleDraft, editor, toast]);

  // The agent reasons over the LIVE (possibly unsaved) timeline — edlRef is
  // always current, so it sees exactly what the user sees.
  const getEdl = useCallback(() => edlRef.current, []);

  // Each streamed agent edit replays through the SAME command bus as manual
  // edits, so it is undoable and keeps the EDL the single source of truth. A
  // lightweight cursor marks where the edit lands (pure presentation).
  const onAgentEdit = useCallback(
    (cmd: EditCommand) => {
      const target = computeTarget(cmd, edlRef.current);
      if (target) setAgentCursor(target);
      const err = editor.dispatch(cmd);
      if (err) toast(err, 'error');
    },
    [editor, toast]
  );

  /**
   * 턴 종료.
   *
   * `persisted` 일 때만 «저장됨» 으로 표시한다 — 서버는 턴이 `done` 까지 갔을 때만
   * 최종 타임라인을 DB 에 쓴다. 끊긴 턴을 저장됨으로 표시하면 AI 가 적용한 편집이
   * 저장 버튼도 못 누르고 경고도 없이 사라진다(2026-09-11 점검 H1).
   * 커서는 어느 쪽이든 지운다 — 턴이 끝났으면 가리킬 곳이 없다.
   */
  const onAgentTurnDone = useCallback(
    (persisted: boolean) => {
      if (persisted) editor.markSaved();
      setAgentCursor(null);
    },
    [editor]
  );

  /**
   * 저장.
   *
   * 🔴 **실패를 반드시 말한다.** 예전에는 `try` 가 없어서 저장이 깨지면 아무 일도
   * 일어나지 않았다 — 성공 토스트가 안 뜨는 것 말고는 신호가 없었다. 액세스 토큰은
   * 1시간이고 갱신 로직이 없으므로 «한 시간 편집 → 저장 → 조용히 실패 → 계속 편집 →
   * 탭을 닫는 순간 전부 유실» 이 실제로 일어날 수 있는 경로였다
   * (2026-09-11 점검: 저장 API 를 500·401·오프라인으로 막아도 화면이 아무 말도 안 했다).
   */
  const handleSave = useCallback(async (): Promise<boolean> => {
    if (!onSave) return true;
    try {
      // 서버가 컷 변경을 감지해 발화 자막을 다시 깔면 갱신된 EDL을 돌려준다.
      // 그걸 반영하지 않으면 자막 없는 로컬 사본이 다음 저장에서 서버를 덮어쓴다.
      const next = await onSave(editor.edl);
      if (next) editor.replaceSaved(next);
      else editor.markSaved();
      toast('저장됨', 'success');
      return true;
    } catch (e) {
      /*
       * 저장 안 된 편집은 그대로 남긴다(dirty 유지) — 다시 눌러 볼 수 있어야 한다.
       * 다시 throw 하지 않는 이유: 버튼이 `onClick={handleSave}` 로 바로 부르므로
       * 던지면 그대로 unhandled rejection 이 된다. 성공 여부는 값으로 돌려준다.
       */
      onError(humanError(e, '저장하지 못했어요. 연결을 확인하고 다시 눌러 주세요.'));
      return false;
    }
  }, [onSave, editor, toast, onError]);

  /**
   * 내보내기 — **먼저 저장한다.**
   *
   * 서버는 export 요청 바디를 받지 않고 DB에 저장된 EDL을 렌더한다. 저장하지 않은
   * 편집이 있으면 사용자가 화면에서 보는 타임라인과 다른 mp4가 나오는데, 렌더는
   * "성공"으로 끝나고 탭은 닫히므로 그 사실을 알 방법이 없다.
   */
  const handleExport = useCallback(async () => {
    if (!onExport) return;
    try {
      // 저장이 실패하면 **내보내지 않는다** — 서버는 DB 의 EDL 을 렌더하므로
      // 화면과 다른 mp4 가 나오고, 그 사실을 알 방법이 없다.
      if (editor.dirty && onSave && !(await handleSave())) return;
      await onExport();
    } catch (e) {
      onError(humanError(e, '내보내지 못했어요. 잠시 뒤 다시 시도해 주세요.'));
    }
  }, [onExport, onSave, editor.dirty, handleSave, onError]);

  // Keyboard shortcuts.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      const player = engine?.player;
      const frame = frameRef.current;
      const sel = selRef.current;

      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (agentBusyRef.current) return; // locked during an agent turn
        e.shiftKey ? editor.redo() : editor.undo();
        return;
      }
      switch (e.key) {
        case ' ':
          e.preventDefault();
          player?.toggle();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          player?.seek(frame - (e.shiftKey ? 10 : 1));
          break;
        case 'ArrowRight':
          e.preventDefault();
          player?.seek(frame + (e.shiftKey ? 10 : 1));
          break;
        case ',':
          player?.seek(frame - 1);
          break;
        case '.':
          player?.seek(frame + 1);
          break;
        case 'Home':
          e.preventDefault();
          player?.seek(0);
          break;
        case 'End':
          e.preventDefault();
          player?.seek(editor.edl.durationInFrames - 1);
          break;
        case 's':
          if (sel && !agentBusyRef.current)
            editor.dispatch({ op: 'split', itemId: sel, atFrame: frame });
          break;
        case 'Delete':
        case 'Backspace':
          if (sel && !agentBusyRef.current) {
            e.preventDefault();
            editor.dispatch({ op: 'remove', itemId: sel });
            setSelectedId(null);
          }
          break;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [engine, editor]);

  /**
   * 설정 패널의 «동작»을 실행하고 돌려받은 커맨드를 커맨드 버스로 재생한다.
   *
   * 서버가 EDL 을 통째로 돌려주지 않고 커맨드를 주는 이유는 AI 패널과 같다 —
   * 여기서 재생해야 실행취소가 이어지고, 화면과 서버가 갈리지 않는다.
   */
  const runSettingsAction = useCallback(
    async (run: (edl: Edl) => Promise<EditCommand[]>, emptyMessage: string) => {
      if (settingsBusy) return;
      setSettingsBusy(true);
      try {
        const commands = await run(getEdl());
        if (commands.length === 0) {
          // 아무 일도 안 일어났는데 조용히 끝내면 «눌렀는데 안 된다»가 된다.
          toast(emptyMessage, 'error');
          return;
        }
        for (const cmd of commands) {
          const err = editor.dispatch(cmd);
          // 한 커맨드가 거부돼도 나머지는 적용한다 — 자막은 부가물이고, 서버 EDL 과
          // 어긋나면 다음 저장이 맞춰 준다.
          if (err) onError(err);
        }
      } catch (e) {
        onError(humanError(e, '적용하지 못했어요. 잠시 뒤 다시 시도해 주세요.'));
      } finally {
        setSettingsBusy(false);
      }
    },
    [editor, getEdl, onError, settingsBusy, toast]
  );

  /*
   * 지금 얹혀 있는 **우리 얼굴 PIP**. 판별은 `editor-core` 의 `isOurPip` 하나만 쓴다 —
   * 「얼굴 빼기」가 지우는 것과 썸네일이 보여주는 것이 같은 아이템이어야 한다.
   * (broll 트랙에는 사람이 넣은 컷어웨이도 들어올 수 있다. 그건 건드리지 않는다.)
   */
  const brollPip =
    (editor.edl.tracks.find((t) => t.id === DEFAULT_TRACK_IDS.broll)?.items ?? []).find(
      (i): i is ClipItem & { crop: NonNullable<ClipItem['crop']> } =>
        i.type === 'clip' && isOurPip(i)
    ) ?? null;

  /** 소스 비율. 알아내기 전에는 16:9 로 가정하고, 알아내면 그 값으로 다시 계산한다. */
  const [sourceAspect, setSourceAspect] = useState(16 / 9);
  const firstAssetId = editor.edl.tracks
    .find((t) => t.id === DEFAULT_TRACK_IDS.video)
    ?.items.find((i): i is ClipItem => i.type === 'clip')?.source.assetId;
  useEffect(() => {
    if (!firstAssetId) return;
    let cancelled = false;
    void (async () => {
      try {
        const url = await resolver(firstAssetId);
        const a = await probeVideoAspect(url);
        if (!cancelled && a && Number.isFinite(a)) setSourceAspect(a);
      } catch {
        // 못 알아내면 16:9 로 둔다 — 프레이밍을 막지는 않는다.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [firstAssetId, resolver]);

  /*
   * 프레이밍은 **저장된 설정이 아니라 EDL 에서 되읽는다.** 화면이 «지금 어느 쪽»인지를
   * 결과에서 읽어야 «설정은 바뀌었는데 영상은 그대로»가 안 생긴다.
   */
  const framing = readFraming(editor.edl, sourceAspect);

  const settingsApi: SettingsApi | undefined = settings && {
    framing,
    framingKeepWidth: keepWidth(sourceAspect),
    onFramingChange: (next: { mode: FramingMode; x: number }) => {
      const plan = planFraming(editor.edl, { ...next, sourceAspect });
      if (plan.applied === 0) return;
      // 커맨드 버스로 흘려야 실행취소가 이어지고 서버 저장과 갈리지 않는다.
      for (const cmd of plan.commands) {
        const err = editor.dispatch(cmd);
        if (err) onError(err);
      }
    },
    params: settings.params,
    onChange: (next) => {
      /*
       * 자막을 끄면 서버가 EDL 에서 자막을 지워 돌려준다 — 그걸 반영하지 않으면
       * 설정은 꺼졌는데 타임라인에는 자막이 그대로 남는다(그 상태로 내보내면
       * 결과물에도 들어간다). 저장(PATCH) 응답을 다루는 방식과 같다.
       */
      void Promise.resolve(settings.onParamsChange(next)).then((edl) => {
        if (edl) editor.replaceSaved(edl);
      });
    },
    onApplySilence: () => void runSettingsAction(settings.applySilence, '잘라낼 빈 곳이 없어요'),
    onApplyLength: () =>
      void runSettingsAction(
        settings.applyLength,
        '어디를 남길지 판단할 근거가 없어요. AI 에디터에게 말해 보세요.'
      ),
    onDownloadSource: settings.downloadSource,
    // 지금 broll 트랙에 뭔가 얹혀 있으면 «뺄 수 있는 상태»다.
    webcamPipApplied: brollPip !== null,
    /*
     * 얹히는 화면을 **렌더 전에** 보여준다. 판정은 하지 않는다 — 실사용자가 자동 감지를
     * 명시적으로 반대했다: "잘못 판정하면 켜져 있는 얼굴을 빼먹는다. 그게 더 나쁘다."
     */
    webcamPipPreview: brollPip && (
      <WebcamPipPreview
        resolver={resolver}
        assetId={brollPip.source.assetId}
        crop={brollPip.crop}
        atSec={(brollPip.source.sourceInSec + brollPip.source.sourceOutSec) / 2}
        width={96}
      />
    ),
    onRemoveWebcamPip: () => {
      /*
       * 🔴 **우리가 얹은 것만** 지운다. 예전엔 broll 트랙을 통째로 비웠는데, 그러면 사람이
       * 넣어 둔 컷어웨이까지 같이 날아간다 — 2라운드 적대적 리뷰에서 나온 결함과 같은 종류다.
       */
      const items = (
        editor.edl.tracks.find((t) => t.id === DEFAULT_TRACK_IDS.broll)?.items ?? []
      ).filter((i) => i.type === 'clip' && isOurPip(i));
      for (const it of items) {
        const err = editor.dispatch({ op: 'remove', itemId: it.id });
        if (err) onError(err);
      }
    },
    onColdOpen: () =>
      void runSettingsAction(
        settings.applyColdOpen,
        '맨 앞에 붙일 만한 순간을 못 찾았어요. 이미 초반에 터지는 클립이면 필요 없어요.'
      ),
    onWebcamPip: () =>
      void runSettingsAction(
        settings.applyWebcamPip,
        settings.webcamConfigured
          ? '얼굴을 얹지 못했어요'
          : '쇼츠 공통설정에서 웹캠 위치를 먼저 등록해 주세요'
      ),
    onDownloadSrt: settings.downloadSrt,
    hasBeats: settings.hasBeats,
    captionAudit: settings.captionAudit,
    onOpenVocabulary: () => setVocabOpen(true),
    silencePreview: settings.silencePreview,
    webcamConfigured: settings.webcamConfigured,
    busy: settingsBusy || agentBusy,
    currentDurationSec: editor.edl.durationInFrames / editor.edl.fps,
  };

  return (
    <div className="flex h-screen flex-col bg-background text-foreground">
      {/* Header */}
      {/*
        헤더는 한 줄이다. 「저장 안 됨」·「AI 편집 중」 배지가 뜨면 폭이 모자라
        버튼 글자가 세로로 쪼개졌다(1512px 에서도 「유튜브에 올리 기」로 깨졌다 —
        2026-09-11 프로덕션 녹화에서 확인). 남는 공간을 **제목만** 내주고
        나머지는 `shrink-0` 로 못 박는다.
      */}
      <header className="flex items-center gap-3 border-b px-4 py-2">
        <h1 className="min-w-0 flex-1 truncate text-sm font-semibold" title={title}>
          {title}
        </h1>
        {/* 기준 숫자가 없으면 «몇 초로 만들지»를 정할 수 없다(스트리머 리뷰 §10.3-A). */}
        <span className="shrink-0 whitespace-nowrap text-xs tabular-nums text-muted-foreground">
          {(editor.edl.durationInFrames / editor.edl.fps).toFixed(1)}초
        </span>
        {agentBusy ? (
          <span className="shrink-0 whitespace-nowrap text-xs text-[hsl(var(--ai))]">
            ✦ AI 편집 중…
          </span>
        ) : (
          editor.dirty && (
            <span className="shrink-0 whitespace-nowrap text-xs text-amber-400">● 저장 안 됨</span>
          )
        )}

        {/* Layout preset toggle */}
        <div className="ml-2 flex shrink-0 items-center gap-1" aria-label="레이아웃">
          {LAYOUT_PRESETS.map((l) => (
            <button
              key={l}
              type="button"
              disabled={agentBusy}
              onClick={() => setLayout(l)}
              className={`shrink-0 whitespace-nowrap rounded-md px-2.5 py-1.5 text-xs disabled:opacity-40 ${
                editor.edl.layout === l
                  ? 'bg-primary font-semibold text-primary-foreground'
                  : 'border'
              }`}
            >
              {LAYOUT_LABELS[l]}
            </button>
          ))}
        </div>
        {/* Title input (per-project) */}
        <input
          type="text"
          value={titleDraft}
          disabled={agentBusy}
          onChange={(e) => setTitleDraft(e.target.value)}
          onBlur={commitTitle}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
          }}
          placeholder="제목 입력"
          maxLength={200}
          className="w-44 shrink-0 rounded-md border bg-transparent px-2 py-1.5 text-xs disabled:opacity-40"
        />

        <div className="min-w-0 flex-1" />
        <button
          type="button"
          onClick={() => setShowGuide((v) => !v)}
          aria-pressed={showGuide}
          title="유튜브 앱이 가리는 자리를 보여줘요"
          className={`flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md border px-2.5 py-1.5 text-xs ${
            showGuide ? 'bg-secondary' : 'text-muted-foreground'
          }`}
        >
          <span
            className={`h-1.5 w-1.5 rounded-full ${
              showGuide ? 'bg-foreground' : 'bg-muted-foreground/50'
            }`}
          />
          가이드
        </button>
        <button
          type="button"
          onClick={editor.undo}
          disabled={!editor.canUndo || agentBusy}
          className="shrink-0 whitespace-nowrap rounded-md border px-2.5 py-1.5 text-xs disabled:opacity-40"
        >
          ↶ 실행취소
        </button>
        <button
          type="button"
          onClick={editor.redo}
          disabled={!editor.canRedo || agentBusy}
          className="shrink-0 whitespace-nowrap rounded-md border px-2.5 py-1.5 text-xs disabled:opacity-40"
        >
          ↷ 다시실행
        </button>
        {onSave && (
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !editor.dirty || agentBusy}
            className="shrink-0 whitespace-nowrap rounded-md border px-3 py-1.5 text-xs disabled:opacity-40"
          >
            {saving ? '저장 중…' : '저장'}
          </button>
        )}
        {onExport && (
          <button
            type="button"
            onClick={() => void handleExport()}
            disabled={saving || agentBusy}
            className="shrink-0 whitespace-nowrap rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground disabled:opacity-40"
          >
            유튜브에 올리기{exportStatus ? ` · ${exportStatus}` : ''}
          </button>
        )}
        {/* 에디터는 다크가 기본이다(브랜드 결정) — 그래도 바꿀 길은 열어 둔다. */}
        <ThemeToggle className="shrink-0" />
      </header>

      {banner}

      {/* Body: 2 columns. Left — the 9:16 preview hugs its width and takes the
          full body height. Right — the AI editor (top) stacked over the
          timeline (bottom). */}
      <div className="flex min-h-0 flex-1 gap-3 p-3">
        {/* Column 1: preview. There is no transport bar — click the preview (or
            press Space) to play/pause; the ruler shows/scrubs the position. */}
        <div className="min-h-0 shrink-0">
          {engine ? (
            <PreviewPane
              engine={engine}
              edl={editor.edl}
              playing={playing}
              onToggle={() => engine.player.toggle()}
              showGuide={showGuide}
              channelName={branding?.channelName}
            />
          ) : (
            <div className="flex h-full items-center justify-center rounded-lg bg-black text-sm text-muted-foreground [aspect-ratio:9/16]">
              미리보기 엔진 초기화 중…
            </div>
          )}
        </div>

        {/* Column 2: 설정 / AI 탭 + 타임라인 */}
        <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-2.5">
          {settings && agent && (
            <div className="flex flex-none items-center gap-1">
              {(
                [
                  ['settings', '설정'],
                  ['agent', 'AI 에디터'],
                ] as const
              ).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setRightTab(key)}
                  className={`rounded-full px-3.5 py-1.5 text-xs transition-colors ${
                    rightTab === key
                      ? 'bg-primary font-semibold text-primary-foreground'
                      : 'border text-muted-foreground hover:bg-secondary hover:text-foreground'
                  }`}
                >
                  {label}
                </button>
              ))}
              <div className="min-w-0 flex-1" />
              <span className="text-[0.7rem] text-muted-foreground">
                {rightTab === 'settings' ? '고른 값은 저장돼요' : '길이·자막은 「설정」에서 정해요'}
              </span>
            </div>
          )}

          {/* Row 1: 패널 — 남는 세로 공간을 전부 쓴다. 항목 편집은 타임라인 우클릭 메뉴에
              있고, 추가 동작만 타임라인 위 작은 바에 있다(아래). */}
          {settings && (rightTab === 'settings' || !agent) && (
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              {settingsApi && <SettingsPanel {...settingsApi} />}
            </div>
          )}
          {agent && (rightTab === 'agent' || !settings) && (
            <div className="flex min-h-0 min-w-0 flex-1 flex-col">
              <AgentPanel
                chat={agent}
                getEdl={getEdl}
                onEdit={onAgentEdit}
                onBusyChange={setAgentBusy}
                onTurnDone={onAgentTurnDone}
                onError={onError}
              />
            </div>
          )}

          {/* Row 2: timeline — claims its content height; the AI editor above
              takes whatever vertical space remains. Without an agent (dev page)
              it sits at the bottom. */}
          <div className={`shrink-0 ${agent || settings ? '' : 'mt-auto'}`}>
            {engine && (
              <div className={agentBusy ? 'pointer-events-none opacity-60' : ''}>
                <Timeline
                  edl={editor.edl}
                  player={engine.player}
                  pool={engine.pool}
                  selectedId={selectedId}
                  onSelect={setSelectedId}
                  dispatch={editor.dispatch}
                  onError={onError}
                  agentCursor={agentCursor}
                  onAddCaption={() => setCaptionOpen(true)}
                  onAddGif={gifSearch ? () => setGifOpen(true) : undefined}
                  sourceDurationSec={sourceDurationSec}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {vocabOpen && settings && (
        <VocabularyModal api={settings.vocabulary} onClose={() => setVocabOpen(false)} />
      )}

      {captionOpen && (
        <CaptionModal
          defaultDurationFrames={DEFAULT_CAPTION_FRAMES}
          onClose={() => setCaptionOpen(false)}
          onSubmit={(text, durationInFrames) => {
            const err = editor.dispatch({
              op: 'addCaption',
              // 사람 입력은 항상 caption-2. caption-1은 기계 소유라 재생성 때 지워진다.
              trackId: DEFAULT_TRACK_IDS.captionOverlay,
              text,
              from: currentFrame,
              durationInFrames,
            });
            if (err) onError(err);
            setCaptionOpen(false);
          }}
        />
      )}

      {gifOpen && gifSearch && (
        <GifSearchModal
          search={gifSearch}
          onClose={() => setGifOpen(false)}
          onSelect={async (gif) => {
            setGifOpen(false);
            // Place the GIF at its own natural length (it loops if later extended).
            const natural = await probeVideoDurationFrames(gif.mp4Url, DEFAULT_GIF_FRAMES);
            const frames = Math.min(MAX_GIF_FRAMES, Math.max(MIN_GIF_FRAMES, natural));
            const err = editor.dispatch({
              op: 'addGif',
              trackId: OVERLAY_TRACK,
              src: gif.mp4Url,
              at: frameRef.current,
              durationInFrames: frames,
              rect: DEFAULT_GIF_RECT,
            });
            if (err) onError(err);
          }}
        />
      )}
    </div>
  );
}
